<template>
  <div class="min-h-screen flex items-center justify-center bg-base-200">
    <div class="card w-96 bg-base-100 shadow-xl">
      <div class="card-body text-center">
        <div v-if="isProcessing">
          <div class="my-5">
            <the-loader :message="`Connecting ${providerName}`" />
          </div>
          <p class="text-base-content/60">
            Processing your {{ providerName }} connection...
          </p>
        </div>

        <div v-else-if="error">
          <h2 class="card-title justify-center mb-2 text-error">Connection Failed</h2>
          <p class="text-base-content/60 mb-4">
            {{ error }}
          </p>
          <div class="card-actions justify-center">
            <button class="btn btn-primary" @click="goToIntegrations">
              Try Again
            </button>
          </div>
        </div>

        <div v-else>
          <h2 class="card-title justify-center mb-2 text-success">Connected Successfully!</h2>
          <p class="text-base-content/60 mb-4">
            Your {{ providerName }} integration is now active. SupaPM can now monitor your email threads for status updates.
          </p>
          <div class="card-actions justify-center">
            <button class="btn btn-primary" @click="goToIntegrations">
              View Integrations
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { SupabaseClient } from '@supabase/supabase-js'

definePageMeta({
  layout: 'default'
})

const route = useRoute()
const router = useRouter()
const { $supabase } = useNuxtApp()
const supabase = $supabase as SupabaseClient
const config = useRuntimeConfig()

const isProcessing = ref(true)
const error = ref<string | null>(null)
const companyId = ref<string | null>(null)
const provider = ref<'gmail' | 'outlook' | null>(null)

const providerName = computed(() => {
  if (provider.value === 'gmail') return 'Gmail'
  if (provider.value === 'outlook') return 'Outlook'
  return 'Email'
})

const goToIntegrations = () => {
  if (companyId.value) {
    router.push(`/company/${companyId.value}/integrations`)
  } else {
    router.push('/company')
  }
}

onMounted(async () => {
  // Get OAuth parameters from URL
  const code = route.query.code as string
  const state = route.query.state as string
  const oauthError = route.query.error as string

  if (oauthError) {
    error.value = `OAuth error: ${oauthError}`
    isProcessing.value = false
    return
  }

  if (!code) {
    error.value = 'No authorization code received'
    isProcessing.value = false
    return
  }

  if (!state) {
    error.value = 'No state parameter received. Please try connecting again.'
    isProcessing.value = false
    return
  }

  try {
    // Parse the state parameter to get company ID and provider
    const stateData = JSON.parse(decodeURIComponent(state))
    companyId.value = stateData.company_id
    provider.value = stateData.provider

    if (!companyId.value) {
      error.value = 'Invalid state parameter. Please try connecting again.'
      isProcessing.value = false
      return
    }

    if (!provider.value || !['gmail', 'outlook'].includes(provider.value)) {
      error.value = 'Invalid provider. Please try connecting again.'
      isProcessing.value = false
      return
    }

    // Verify user is still logged in
    const session = await supabase.auth.getSession()
    if (!session.data.session) {
      error.value = 'Please log in to complete the integration'
      isProcessing.value = false
      return
    }

    // Use the static callback URL for the OAuth exchange
    const redirectUri = `${config.public.siteUrl}/integrations/email/callback`

    const response = await $fetch(`/api/v1/${companyId.value}/integrations/email/connect`, {
      method: 'POST',
      headers: {
        authorization: `Bearer ${session.data.session.access_token}`
      },
      body: {
        code,
        redirect_uri: redirectUri,
        provider: provider.value
      }
    })

    if (response.status === 'success') {
      // Success! Redirect to integrations page after a short delay
      setTimeout(() => {
        goToIntegrations()
      }, 2000)
    } else {
      error.value = response.message || 'Failed to connect email'
    }

  } catch (err: any) {
    console.error('Email connection error:', err)
    if (err.message?.includes('JSON')) {
      error.value = 'Invalid state parameter. Please try connecting again.'
    } else {
      error.value = err.statusMessage || err.message || 'Failed to connect email'
    }
  } finally {
    isProcessing.value = false
  }
})
</script>

